// frontend/src/components/RequireAuth.jsx
//
// Wrapper de rota: se o usuário NÃO está logado, redireciona pra /login.
// Uso:
//   <Route path="/leiloes" element={<RequireAuth><Leiloes/></RequireAuth>} />
//   <Route path="/admin" element={<RequireAuth role="admin"><AdminLayout/></RequireAuth>}>
//
// IMPORTANTE: avalia de forma OTIMISTA usando sessionStorage (síncrono) — não
// espera o AuthContext.hydrateMe() resolver. Sem isso, qualquer F5 em rota
// protegida joga o usuário pra /login antes do /api/me responder.
// A expiração é checada antes de renderizar; respostas 401 também limpam a sessão.
import React from 'react';
import { tokenExpired } from '../utils/sessionExpiry';
import { clearSession } from '../utils/session';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function readUser() {
  try {
    const raw = sessionStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Telas que continuam abertas depois do teste vencer: são justamente o caminho
// para voltar a ter acesso. Sem esta lista, /plano-expirado se bloquearia a si
// mesma e o usuário entraria num laço de redirecionamento.
const LIBERADAS_APOS_VENCER = ['/plano-expirado', '/planos', '/pagamento-confirmado'];

export default function RequireAuth({ children, role }) {
  const { auth } = useAuth() || {};
  const location = useLocation();

  const token = sessionStorage.getItem('token');
  // FONTE DA VERDADE = sessionStorage (atualizado de forma síncrona no login e
  // no logout). O auth.user do contexto é só fallback de reatividade — NUNCA
  // pode ter prioridade, senão um estado stale (ex: admin que deslogou) "gruda"
  // e libera rota protegida pro usuário errado. Por isso readUser() vem 1º.
  const user = readUser() || auth?.user;
  const isAuthenticated = !!token && !!user;

  if (token && tokenExpired(token)) {
    clearSession();
    return <Navigate to="/login?motivo=sessao-expirada" state={{ from: location }} replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const educacaoPath=/^\/admin\/(?:educacao(?:\/|$)|membros(?:\/|$))/.test(location.pathname);
  if(user.role==='educacao'&&!educacaoPath)return <Navigate to="/admin/educacao/produtos" replace/>;
  // Perfil marketing: só números, relatórios, banners, materiais, versão da landing e leads.
  const marketingPath=/^\/admin\/(?:dashboard|relatorios|banners|materiais|landing|leads)(?:\/|$)/.test(location.pathname);
  if(user.role==='marketing'&&!marketingPath)return <Navigate to="/admin/dashboard" replace/>;
  // Gate por role (ex: <RequireAuth role="admin">). Aceita admin OU moderador
  // quando role="admin", admin only quando role="admin-strict".
  if (role) {
    const userRole = user.role || 'usuario';
    const allowed =
      role === 'admin' ? ['admin', 'moderador','educacao','marketing'].includes(userRole) :
      role === 'educacao' ? ['admin','educacao'].includes(userRole) :
      role === 'admin-strict' ? userRole === 'admin' :
      userRole === role;
    if (!allowed) {
      return <Navigate to="/" replace />;
    }
  }

  // ===== Teste encerrado =====
  // Antes esta checagem vivia só no Painel (/dashboard), então quem tinha o
  // teste vencido continuava entrando na busca, no imóvel, na calculadora e no
  // mapa — e, como quase ninguém entra pelo Painel, muita gente sequer ficava
  // sabendo que o período havia acabado.
  //
  // A fonte da verdade é o /api/me: o backend calcula o plano efetivo levando
  // em conta assinatura paga, então 'free' aqui significa "teste acabou e não
  // assinou". Enquanto o /api/me não respondeu, deixamos passar — mesma postura
  // otimista do resto do arquivo, para não expulsar ninguém num F5.
  const trialEncerrado = auth?.user?.plan === 'free' && auth?.trialActive === false;
  // Cursos têm matrícula própria, independente da assinatura da busca.
  const areaMembros = /^\/dashboard\/(?:membros|vitrine)(?:\/|$)/.test(location.pathname);
  const rotaLiberada = areaMembros || LIBERADAS_APOS_VENCER.some((r) => location.pathname.startsWith(r));

  if (trialEncerrado && !rotaLiberada && !['educacao','marketing'].includes(user.role)) {
    return <Navigate to="/plano-expirado" replace />;
  }

  return children;
}

